import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import {
  Crown, Check, Loader2, ArrowLeft, Sparkles,
  Stethoscope, Wallet, UserX, Zap, Upload, Copy, CheckCircle, Clock, XCircle, IndianRupee,
  Globe, Calendar, Camera, BarChart3, Lock, Smartphone, MapPin, Star, Phone, MessageSquare,
  FileText
} from 'lucide-react';

const FREE_FEATURES = [
  { icon: Globe, label: 'Personal Doctor Website' },
  { icon: Calendar, label: 'Multi-Location Scheduling' },
  { icon: MapPin, label: 'Unlimited Practice Locations' },
  { icon: Clock, label: 'Real-Time Slot Management' },
  { icon: Camera, label: 'Photo Upload & Optimization' },
  { icon: BarChart3, label: 'Website Readiness Score · Publish Gate' },
  { icon: CheckCircle, label: 'Consult Completed Tracking' },
  { icon: Star, label: 'Patient Appointment Booking' },
  { icon: Lock, label: 'Legal Protection & Consent Flow' },
  { icon: Smartphone, label: 'PWA Offline Browsing' },
  { icon: UserX, label: 'No AI assistance — you hand-write every section', desc: 'Free preserves authentic voice. Coffee adds a one-time AI on-ramp; Turbo unlocks monthly AI drafts.' },
];

const PREMIUM_FEATURES = [
  { icon: Stethoscope, label: 'One-Time AI On-Ramp · Bio + Procedures + FAQ', desc: '3 lifetime AI pulls — let GPT seed your profile sections, then edit in your voice. After the 3 pulls, you continue manually.' },
  { icon: Wallet, label: 'Doctor Wallet & Appointment Codes', desc: 'Unique appointment code per doctor' },
  { icon: UserX, label: 'Did Not Attend (DNA) Tracking', desc: 'Auto-refund wallet, reliability scoring' },
  { icon: Zap, label: 'Priority Listing & Premium Badge' },
  { icon: MessageSquare, label: 'Direct Patient Chat — up to 3 messages', desc: 'Each patient can message you up to 3 times per appointment' },
  { icon: Lock, label: 'Encrypted Data Export', desc: 'Your interaction data — encrypted, always accessible to you' },
];

const TURBO_FEATURES = [
  { icon: Sparkles, label: 'Monthly AI Pulls (2/month)', desc: 'Across SEO articles, procedure blend, case-study check, address parser, voice-meter regenerate' },
  { icon: FileText, label: 'AI SEO Articles · Auto-drafted Monthly', desc: 'Long-tail symptom + city content on your slug — Turbo-only' },
  { icon: BarChart3, label: 'Full Lead Tracking Dashboard', desc: 'Page views, WhatsApp/phone clicks, source breakdown' },
  { icon: Globe, label: 'Schema.org + Auto Meta Tags', desc: 'Physician + FAQPage + Breadcrumb JSON-LD' },
  { icon: Camera, label: 'Clinic Kit — Printable QR Poster + Stickers', desc: 'A4 poster + 3 sticker sizes + offline vCard QR' },
  { icon: Star, label: 'Save My Details QR Widget', desc: 'Floating vCard QR on your public site — always-on' },
  { icon: Zap, label: 'Turbo Visibility Accelerator Checklist', desc: '5-Links Challenge + guided backlink wizard' },
];

const COMING_SOON = [
  { icon: Phone, label: 'IVR Missed-Call Booking', desc: 'Patients book via missed call' },
  { icon: MessageSquare, label: 'SMS-Based Booking', desc: 'Patients book via SMS with your code' },
];

const EARLY_ADOPTER_NOTE = "";  // No early-adopter discount on Premium. Pricing is the standard rate.

const INDIA_COMPETITORS = [
  { name: 'Typical directory listing', price: 'Rs. 5,000–15,000/mo', features: ['Listing only (no own website)', 'Basic scheduling'], missing: ['No own website', 'No AI', 'No data export'] },
  { name: 'Free Q&A platform', price: 'Free + Commission', features: ['Q&A listing', 'Basic profile'], missing: ['No website', 'No scheduling'] },
];

const GLOBAL_COMPETITORS = [
  { name: 'US booking marketplace', region: 'USA', price: '$35–180/booking', features: ['Search listing', 'Scheduling'], missing: ['No own website', 'No AI'] },
  { name: 'US practice management SaaS', region: 'USA', price: '$99–399/mo', features: ['Website builder', 'SEO'], missing: ['No AI', 'No wallet'] },
];

export default function UpgradePage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [upiId, setUpiId] = useState('');
  const [upiPayeeName, setUpiPayeeName] = useState('');
  const [subStatus, setSubStatus] = useState(null);
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [pageLoading, setPageLoading] = useState(true);
  const [step, setStep] = useState('select');
  const [utrNumber, setUtrNumber] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [copied, setCopied] = useState(false);

  // Coupon redemption inline on the Pay screen — saves doctors who have a
  // privileged-access code from going through UPI/upload at all.
  const [couponCode, setCouponCode] = useState('');
  const [couponMsg, setCouponMsg] = useState('');
  const [couponErr, setCouponErr] = useState('');
  const [couponBusy, setCouponBusy] = useState(false);
  const [couponApplied, setCouponApplied] = useState(false);

  const redeemCoupon = async () => {
    const code = couponCode.trim().toUpperCase();
    if (!code) { setCouponErr('Enter your coupon code'); return; }
    setCouponBusy(true);
    setCouponErr(''); setCouponMsg('');
    try {
      const { data } = await api.post('/dashboard/redeem-coupon', { code });
      setCouponMsg(`Activated! Your account is now on ${data.tier?.toUpperCase() || 'PREMIUM'} until ${new Date(data.new_expiry).toLocaleDateString()}. No payment needed.`);
      setCouponApplied(true);
    } catch (e) {
      setCouponErr(e.response?.data?.detail || e.message);
    } finally { setCouponBusy(false); }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'doctor') { navigate('/'); return; }
    Promise.all([
      api.get('/subscriptions/plans'),
      api.get('/subscriptions/status'),
      api.get('/subscriptions/payment-status'),
    ]).then(([plansRes, statusRes, payStatusRes]) => {
      setPlans(plansRes.data.plans);
      setUpiId(plansRes.data.upi_id || '');
      setUpiPayeeName(plansRes.data.upi_payee_name || '');
      setSubStatus(statusRes.data);
      setPaymentRequest(payStatusRes.data.payment_request);
    }).catch(() => {}).finally(() => setPageLoading(false));
  }, [user, loading, navigate]);

  const copyUpi = () => { navigator.clipboard.writeText(upiId); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  const handleSubmitPayment = async () => {
    if (!utrNumber.trim()) { setMessage('Error: Please enter the UTR/Transaction reference number'); return; }
    if (!screenshot) { setMessage('Error: Please upload the payment screenshot'); return; }
    setSubmitting(true); setMessage('');
    try {
      const formData = new FormData();
      formData.append('plan', selectedPlan);
      formData.append('utr_number', utrNumber.trim());
      formData.append('screenshot', screenshot);
      await api.post('/subscriptions/submit-upi-payment', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setStep('submitted');
      setMessage('Payment verification submitted successfully!');
    } catch (e) { setMessage('Error: ' + formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  if (loading || pageLoading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-sky-600" size={32} /></div>;

  const isPremium = subStatus?.is_premium;
  const currentTier = subStatus?.tier || (user?.tier || 'free');
  const isOnTurbo = currentTier === 'turbo';
  const activeSub = subStatus?.subscription;
  const selectedPlanData = plans.find(p => p.id === selectedPlan);

  // Premium active view — but offer a clear path to TurboCharge so the
  // doctor can jump tiers without having to leave this screen. Hides for
  // doctors already on Turbo (top tier). Skipped when the doctor has
  // moved into the pay/upload/submitted flow (so Premium → Turbo upgrade
  // can render its own UPI/upload screens). Also skipped when there's a
  // pending or rejected payment request, so those views can render.
  if (isPremium && !isOnTurbo && step === 'select' && !paymentRequest) {
    return (
      <div className="min-h-screen bg-slate-50/50" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-10">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6" data-testid="back-to-dashboard"><ArrowLeft size={16} /> Back to Dashboard</button>
          <div className="space-y-6" data-testid="active-subscription">
            <div className="bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 rounded-2xl border border-amber-200 p-8 text-center">
              <Crown size={36} className="text-amber-500 mx-auto mb-3" />
              <h2 className="text-2xl font-bold text-amber-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Coffee Premium Active</h2>
              <p className="text-sm text-amber-700 mt-2">You have full access to all Premium features.</p>
              {subStatus?.warning && <p className="text-sm text-red-600 mt-3 font-medium">{subStatus.warning}</p>}
            </div>
            {activeSub && (
              <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
                <h3 className="text-sm font-medium text-slate-900">Subscription Details</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><p className="text-xs text-slate-500">Plan</p><p className="font-medium">{activeSub.plan_name}</p></div>
                  <div><p className="text-xs text-slate-500">Amount</p><p className="font-medium">Rs. {(activeSub.amount / 100).toFixed(0)}</p></div>
                  <div><p className="text-xs text-slate-500">Started</p><p className="font-medium">{activeSub.start_date?.split('T')[0]}</p></div>
                  <div><p className="text-xs text-slate-500">Expires</p><p className="font-medium">{activeSub.end_date?.split('T')[0]}</p></div>
                </div>
              </div>
            )}

            {/* Premium → Turbo upgrade CTA card. Lets the doctor jump
                tiers without bouncing back through plan selection, AND
                redeem a Turbo-tier coupon inline. */}
            <div className="rounded-2xl border-2 border-fuchsia-300 bg-gradient-to-br from-fuchsia-50/60 via-white to-amber-50/40 p-6 sm:p-7" data-testid="upgrade-to-turbo-card">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-fuchsia-500 to-rose-500 flex items-center justify-center flex-shrink-0">
                  <Sparkles size={20} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold text-fuchsia-700 uppercase tracking-widest">Take it further</p>
                  <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Upgrade to TurboCharge</h3>
                  <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">Unlock the full SEO + lead-tracking engine — AI articles on your slug, lead-source breakdown, Clinic Kit, vCard QR widget and more.</p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-2 mb-5">
                {TURBO_FEATURES.slice(0, 6).map(({ label }) => (
                  <div key={label} className="flex items-start gap-2">
                    <Sparkles size={13} className="text-fuchsia-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs font-medium text-slate-800">{label}</span>
                  </div>
                ))}
              </div>

              {/* Inline coupon redemption — privileged-access fast-path. */}
              {!couponApplied ? (
                <div className="rounded-xl border border-violet-200 bg-violet-50/60 p-4 mb-4" data-testid="premium-turbo-coupon-block">
                  <p className="text-[10px] font-bold text-violet-700 uppercase tracking-widest mb-1.5">Have a Turbo coupon?</p>
                  <p className="text-xs text-slate-600 mb-3 leading-relaxed">Got a privileged-access code for TurboCharge? Enter it to activate Turbo without paying again.</p>
                  <div className="flex gap-2">
                    <input type="text" value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="ENTER CODE"
                      className="flex-1 px-3 py-2.5 rounded-lg border border-violet-300 bg-white text-sm font-mono tracking-wider focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 outline-none"
                      data-testid="premium-turbo-coupon-input"
                      onKeyDown={(e) => { if (e.key === 'Enter') redeemCoupon(); }}
                    />
                    <button onClick={redeemCoupon} disabled={couponBusy || !couponCode.trim()}
                      className="px-4 py-2.5 rounded-lg bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 flex items-center gap-1.5"
                      data-testid="premium-turbo-coupon-apply">
                      {couponBusy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                      Apply
                    </button>
                  </div>
                  {couponErr && <p className="text-xs text-red-600 mt-2" data-testid="premium-turbo-coupon-error">{couponErr}</p>}
                </div>
              ) : (
                <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4 text-center mb-4" data-testid="premium-turbo-coupon-success">
                  <CheckCircle size={28} className="text-emerald-600 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-emerald-900">{couponMsg}</p>
                  <button onClick={() => navigate('/dashboard')}
                    className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"
                    data-testid="premium-turbo-coupon-go-dashboard">
                    Go to my dashboard
                  </button>
                </div>
              )}

              {!couponApplied && (
                <button onClick={() => { setSelectedPlan('turbo_monthly'); setStep('pay'); }}
                  className="w-full py-3 rounded-full text-white font-bold text-sm flex items-center justify-center gap-2 bg-gradient-to-r from-fuchsia-600 to-rose-500 hover:from-fuchsia-700 hover:to-rose-600 transition-all"
                  data-testid="premium-upgrade-to-turbo-btn">
                  <Sparkles size={16} /> Upgrade to TurboCharge — Rs. 999/mo
                </button>
              )}
              <p className="text-center text-[10px] text-slate-400 mt-2">First 1,000 doctors only — early adopter price</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Top-tier (Turbo) — show the standard Active view, no further upgrade.
  if (isOnTurbo) {
    return (
      <div className="min-h-screen bg-slate-50/50" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-10">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6" data-testid="back-to-dashboard"><ArrowLeft size={16} /> Back to Dashboard</button>
          <div className="space-y-6" data-testid="active-subscription">
            <div className="bg-gradient-to-br from-fuchsia-50 via-white to-amber-50 rounded-2xl border-2 border-fuchsia-200 p-8 text-center">
              <Sparkles size={36} className="text-fuchsia-500 mx-auto mb-3" />
              <h2 className="text-2xl font-bold text-fuchsia-900" style={{ fontFamily: 'Outfit, sans-serif' }}>TurboCharge Active</h2>
              <p className="text-sm text-fuchsia-700 mt-2">You're on the top tier — full SEO + lead-tracking engine is on.</p>
              {subStatus?.warning && <p className="text-sm text-red-600 mt-3 font-medium">{subStatus.warning}</p>}
            </div>
            {activeSub && (
              <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
                <h3 className="text-sm font-medium text-slate-900">Subscription Details</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><p className="text-xs text-slate-500">Plan</p><p className="font-medium">{activeSub.plan_name}</p></div>
                  <div><p className="text-xs text-slate-500">Amount</p><p className="font-medium">Rs. {(activeSub.amount / 100).toFixed(0)}</p></div>
                  <div><p className="text-xs text-slate-500">Started</p><p className="font-medium">{activeSub.start_date?.split('T')[0]}</p></div>
                  <div><p className="text-xs text-slate-500">Expires</p><p className="font-medium">{activeSub.end_date?.split('T')[0]}</p></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Pending payment
  if (paymentRequest && paymentRequest.status === 'pending') {
    return (
      <div className="min-h-screen bg-slate-50/50" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-10">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6" data-testid="back-to-dashboard"><ArrowLeft size={16} /> Back to Dashboard</button>
          <div className="bg-amber-50 rounded-2xl border border-amber-200 p-8 text-center" data-testid="payment-pending">
            <Clock size={36} className="text-amber-500 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-amber-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Payment Verification Pending</h2>
            <p className="text-sm text-amber-700 mt-2 max-w-md mx-auto">Your payment of <strong>Rs. {paymentRequest.display_amount}</strong> for {paymentRequest.plan_name} is being reviewed.</p>
            <div className="mt-4 bg-white rounded-xl p-4 max-w-sm mx-auto text-left text-xs space-y-2">
              <div className="flex justify-between"><span className="text-slate-500">UTR Number</span><span className="font-mono font-medium">{paymentRequest.utr_number}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Submitted</span><span className="font-medium">{paymentRequest.created_at?.split('T')[0]}</span></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Rejected
  if (paymentRequest && paymentRequest.status === 'rejected') {
    return (
      <div className="min-h-screen bg-slate-50/50" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-10">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6" data-testid="back-to-dashboard"><ArrowLeft size={16} /> Back to Dashboard</button>
          <div className="bg-red-50 rounded-2xl border border-red-200 p-8 text-center mb-6" data-testid="payment-rejected">
            <XCircle size={36} className="text-red-400 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-red-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Payment Verification Failed</h2>
            {paymentRequest.admin_note && <p className="text-sm text-red-600 mt-2 bg-white rounded-lg p-3 max-w-md mx-auto">Reason: {paymentRequest.admin_note}</p>}
            <button onClick={() => { setPaymentRequest(null); setStep('select'); }} className="mt-4 px-6 py-2.5 rounded-full bg-sky-600 text-white text-sm font-medium hover:bg-sky-700" data-testid="retry-payment-btn">Try Again</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-10">
        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6" data-testid="back-to-dashboard"><ArrowLeft size={16} /> Back to Dashboard</button>

        {step === 'submitted' ? (
          <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-8 text-center" data-testid="payment-submitted">
            <CheckCircle size={36} className="text-emerald-500 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-emerald-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Payment Submitted for Verification</h2>
            <p className="text-sm text-emerald-700 mt-2">We'll verify your payment and activate Premium shortly.</p>
            <button onClick={() => navigate('/dashboard')} className="mt-4 px-6 py-2.5 rounded-full bg-sky-600 text-white text-sm font-medium hover:bg-sky-700">Go to Dashboard</button>
          </div>
        ) : step === 'upload' ? (
          <div className="space-y-6">
            <h1 className="text-2xl font-bold text-slate-900 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>Verify Your Payment</h1>
            <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5 max-w-lg mx-auto">
              <div className="bg-sky-50 rounded-xl p-4 flex items-center gap-3">
                <IndianRupee size={18} className="text-sky-600" />
                <div><p className="text-sm font-medium text-sky-900">{selectedPlanData?.name} - Rs. {selectedPlanData?.display_price}</p><p className="text-xs text-sky-600">Paid to UPI: {upiId}</p></div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">UTR / Transaction Reference Number *</label>
                <input type="text" className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 outline-none" value={utrNumber} onChange={e => setUtrNumber(e.target.value)} placeholder="e.g., 425631987456" data-testid="utr-input" />
                <p className="text-[10px] text-slate-400 mt-1">Find this in your UPI app's transaction history</p>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">Payment Screenshot *</label>
                <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-sky-300 transition-colors cursor-pointer" onClick={() => document.getElementById('screenshot-input').click()}>
                  {screenshot ? (<div className="space-y-2"><CheckCircle size={24} className="text-emerald-500 mx-auto" /><p className="text-sm font-medium text-emerald-700">{screenshot.name}</p></div>) : (<div className="space-y-2"><Upload size={24} className="text-slate-400 mx-auto" /><p className="text-sm text-slate-600">Click to upload screenshot</p></div>)}
                  <input id="screenshot-input" type="file" accept="image/*" className="hidden" onChange={e => setScreenshot(e.target.files?.[0] || null)} data-testid="screenshot-input" />
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setStep('pay')} className="px-5 py-2.5 rounded-full border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Back</button>
                <button onClick={handleSubmitPayment} disabled={submitting} className="flex-1 py-2.5 rounded-full bg-sky-600 text-white font-semibold text-sm hover:bg-sky-700 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="submit-payment-btn">
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />} {submitting ? 'Submitting...' : 'Submit for Verification'}
                </button>
              </div>
              {message && <p className={`text-center text-sm ${message.startsWith('Error') ? 'text-red-600' : 'text-emerald-600'}`}>{message}</p>}
            </div>
          </div>
        ) : step === 'pay' ? (
          <div className="space-y-6">
            <h1 className="text-2xl font-bold text-slate-900 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>Pay via UPI</h1>
            <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5 max-w-lg mx-auto">

              {/* COUPON CODE — privileged access fast-path. Skips UPI + upload entirely. */}
              {!couponApplied ? (
                <div className="rounded-xl border border-violet-200 bg-violet-50/60 p-4" data-testid="upgrade-coupon-block">
                  <p className="text-[10px] font-bold text-violet-700 uppercase tracking-widest mb-1.5">Have a coupon code?</p>
                  <p className="text-xs text-slate-600 mb-3 leading-relaxed">If you've been given a privileged-access code, enter it below to skip the payment.</p>
                  <div className="flex gap-2">
                    <input type="text" value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="ENTER CODE"
                      className="flex-1 px-3 py-2.5 rounded-lg border border-violet-300 bg-white text-sm font-mono tracking-wider focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 outline-none"
                      data-testid="upgrade-coupon-input"
                      onKeyDown={(e) => { if (e.key === 'Enter') redeemCoupon(); }}
                    />
                    <button onClick={redeemCoupon} disabled={couponBusy || !couponCode.trim()}
                      className="px-4 py-2.5 rounded-lg bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 flex items-center gap-1.5"
                      data-testid="upgrade-coupon-apply">
                      {couponBusy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                      Apply
                    </button>
                  </div>
                  {couponErr && <p className="text-xs text-red-600 mt-2" data-testid="upgrade-coupon-error">{couponErr}</p>}
                </div>
              ) : (
                <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4 text-center" data-testid="upgrade-coupon-success">
                  <CheckCircle size={28} className="text-emerald-600 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-emerald-900">{couponMsg}</p>
                  <button onClick={() => navigate('/dashboard')}
                    className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"
                    data-testid="upgrade-coupon-go-dashboard">
                    Go to my dashboard
                  </button>
                </div>
              )}

              <div className={`text-center py-4 bg-gradient-to-r from-sky-50 to-sky-100/50 rounded-xl ${couponApplied ? 'opacity-50' : ''}`}>
                <p className="text-xs text-sky-600 font-medium uppercase tracking-wider">Amount to Pay</p>
                <p className="text-4xl font-bold text-sky-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Rs. {selectedPlanData?.display_price}</p>
                <p className="text-xs text-sky-600 mt-1">{selectedPlanData?.name}</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-5">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Pay to UPI Address</p>
                <div className="flex items-center gap-3">
                  <span className="text-lg font-bold text-slate-900 font-mono tracking-wide flex-1" data-testid="upi-address">{upiId}</span>
                  <button onClick={copyUpi} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${copied ? 'bg-emerald-100 text-emerald-700' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'}`} data-testid="copy-upi-btn">
                    {copied ? <><CheckCircle size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-2">Payee: {upiPayeeName}</p>
              </div>
              <div className="space-y-2">
                {['Open any UPI app (Google Pay, PhonePe, Paytm, etc.)', `Send Rs. ${selectedPlanData?.display_price} to ${upiId}`, 'Take a screenshot of the payment confirmation', 'Upload the screenshot on the next screen'].map((s, i) => (
                  <div key={i} className="flex items-start gap-3"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-sky-100 text-sky-700 text-xs font-bold flex items-center justify-center">{i + 1}</span><p className="text-sm text-slate-600">{s}</p></div>
                ))}
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setStep('select')} className="px-5 py-2.5 rounded-full border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Back</button>
                <button onClick={() => setStep('upload')} className="flex-1 py-2.5 rounded-full bg-sky-600 text-white font-semibold text-sm hover:bg-sky-700 flex items-center justify-center gap-2" data-testid="paid-proceed-btn"><CheckCircle size={16} /> I've Paid — Upload Proof</button>
              </div>
            </div>
          </div>
        ) : (
          /* PLAN SELECTION with Free vs Premium comparison */
          <div className="space-y-8">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-100 text-amber-800 text-xs font-bold mb-4"><Sparkles size={14} /> CHOOSE YOUR PLAN</div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>See What Others Charge</h1>
              <p className="text-slate-500 mt-3 max-w-2xl mx-auto text-sm">Compare competitors vs DocSite Gold (free) vs Coffee Premium (less than a cup of coffee a day) vs TurboCharge (full SEO + lead-tracking engine).</p>
            </div>

            {/* 4-COLUMN: Competitors | Gold | Coffee Premium | TurboCharge */}
            <div className="grid lg:grid-cols-4 gap-4">
              {/* COLUMN 1: COMPETITORS */}
              <div className="space-y-4">
                <div className="rounded-2xl border border-red-200 bg-red-50/30 p-5">
                  <p className="text-[10px] font-bold text-red-500 uppercase tracking-widest mb-3">India</p>
                  {INDIA_COMPETITORS.map(c => (
                    <div key={c.name} className="mb-3 last:mb-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-bold text-slate-900">{c.name}</p>
                        <span className="text-[10px] font-bold text-red-600">{c.price}</span>
                      </div>
                      {c.features.map(f => <p key={f} className="text-[10px] text-slate-500 flex items-center gap-1"><CheckCircle size={9} className="text-slate-300" /> {f}</p>)}
                      {c.missing.map(f => <p key={f} className="text-[10px] text-red-400 flex items-center gap-1"><XCircle size={9} /> {f}</p>)}
                    </div>
                  ))}
                </div>
                <div className="rounded-2xl border border-red-200 bg-red-50/30 p-5">
                  <p className="text-[10px] font-bold text-red-500 uppercase tracking-widest mb-3">Global</p>
                  {GLOBAL_COMPETITORS.map(c => (
                    <div key={c.name} className="mb-3 last:mb-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-bold text-slate-900">{c.name} <span className="text-[8px] text-slate-400">({c.region})</span></p>
                        <span className="text-[10px] font-bold text-red-600">{c.price}</span>
                      </div>
                      {c.features.map(f => <p key={f} className="text-[10px] text-slate-500 flex items-center gap-1"><CheckCircle size={9} className="text-slate-300" /> {f}</p>)}
                      {c.missing.map(f => <p key={f} className="text-[10px] text-red-400 flex items-center gap-1"><XCircle size={9} /> {f}</p>)}
                    </div>
                  ))}
                </div>
              </div>

              {/* COLUMN 2: GOLD (FREE) */}
              <div className="rounded-2xl border-2 border-amber-300 bg-gradient-to-b from-amber-50/50 to-white p-5 relative" data-testid="free-plan-card">
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-amber-500 text-white text-[10px] font-bold">GOLD</span>
                <div className="text-center mb-4 pt-2">
                  <p className="text-[10px] font-bold text-amber-600 uppercase tracking-widest">DocSite</p>
                  <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Gold</h3>
                  <p className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Rs. 0</p>
                  <p className="text-xs text-slate-500">Free forever</p>
                </div>
                <div className="space-y-2.5">
                  {FREE_FEATURES.map(({ label }) => (
                    <div key={label} className="flex items-center gap-2">
                      <CheckCircle size={13} className="text-emerald-500 flex-shrink-0" />
                      <span className="text-xs text-slate-700">{label}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-5 pt-3 border-t border-amber-200 text-center">
                  <p className="text-[10px] text-slate-400">You're on this plan</p>
                </div>
              </div>

              {/* COLUMN 3: COFFEE PREMIUM */}
              <div className="rounded-2xl border-2 border-sky-400 bg-gradient-to-b from-sky-50/50 to-white p-5 relative" data-testid="premium-plan-card">
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-sky-600 text-white text-[10px] font-bold whitespace-nowrap">POPULAR</span>
                <div className="text-center mb-4 pt-2">
                  <p className="text-[10px] font-bold text-sky-600 uppercase tracking-widest">DocSite</p>
                  <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Coffee Premium</h3>
                  <div className="flex items-baseline justify-center gap-2 mt-1">
                    <p className="text-3xl font-bold text-sky-600" style={{ fontFamily: 'Outfit, sans-serif' }}>Rs. 299</p>
                  </div>
                  <p className="text-xs text-slate-500">/ month</p>
                  <p className="text-[9px] text-amber-600 font-bold mt-1">Costs less than a cup of coffee a day</p>
                </div>
                <div className="space-y-2.5">
                  <p className="text-[9px] font-bold text-emerald-600 uppercase tracking-wider">All Gold features, plus:</p>
                  {PREMIUM_FEATURES.map(({ label, desc }) => (
                    <div key={label} className="flex items-start gap-2">
                      <Crown size={13} className="text-amber-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-xs font-medium text-slate-900">{label}</span>
                        {desc && <p className="text-[9px] text-slate-400">{desc}</p>}
                      </div>
                    </div>
                  ))}
                  <p className="text-[9px] font-bold text-violet-500 uppercase tracking-wider mt-2">Coming Soon:</p>
                  {COMING_SOON.map(({ icon: Icon, label }) => (
                    <div key={label} className="flex items-center gap-2 opacity-60">
                      <Icon size={13} className="text-violet-400 flex-shrink-0" />
                      <span className="text-xs text-slate-600">{label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* COLUMN 4: TURBOCHARGE */}
              <div className="rounded-2xl border-2 border-fuchsia-400 bg-gradient-to-b from-fuchsia-50/60 via-white to-amber-50/40 p-5 relative" data-testid="turbo-plan-card">
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-to-r from-fuchsia-600 to-rose-500 text-white text-[10px] font-bold whitespace-nowrap">RECOMMENDED</span>
                <div className="text-center mb-4 pt-2">
                  <p className="text-[10px] font-bold text-fuchsia-600 uppercase tracking-widest">DocSite</p>
                  <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>TurboCharge</h3>
                  <div className="flex items-baseline justify-center gap-2 mt-1">
                    <p className="text-3xl font-bold text-fuchsia-600" style={{ fontFamily: 'Outfit, sans-serif' }}>Rs. 999</p>
                    <p className="text-sm text-slate-400 line-through">Rs. 1,999</p>
                  </div>
                  <p className="text-xs text-slate-500">/ month</p>
                  <p className="text-[9px] text-fuchsia-700 font-bold mt-1">First 1,000 doctors only — early adopter price</p>
                </div>
                <div className="space-y-2.5">
                  <p className="text-[9px] font-bold text-emerald-600 uppercase tracking-wider">Everything in Premium, plus:</p>
                  {TURBO_FEATURES.map(({ label, desc }) => (
                    <div key={label} className="flex items-start gap-2">
                      <Sparkles size={13} className="text-fuchsia-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-xs font-medium text-slate-900">{label}</span>
                        {desc && <p className="text-[9px] text-slate-400">{desc}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Billing selector — Tier toggle + monthly/annual */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <p className="text-sm font-medium text-slate-900">Pick your plan</p>
                <div className="inline-flex rounded-full bg-slate-100 p-1" data-testid="tier-toggle">
                  {[
                    { id: 'premium', label: 'Coffee Premium', color: 'bg-sky-600' },
                    { id: 'turbo', label: 'TurboCharge', color: 'bg-gradient-to-r from-fuchsia-600 to-rose-500' },
                  ].map(t => {
                    const active = (t.id === 'turbo' && selectedPlan.startsWith('turbo')) || (t.id === 'premium' && !selectedPlan.startsWith('turbo'));
                    return (
                      <button key={t.id}
                        onClick={() => setSelectedPlan(t.id === 'turbo' ? 'turbo_monthly' : 'monthly')}
                        className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${active ? `${t.color} text-white` : 'text-slate-600 hover:text-slate-900'}`}
                        data-testid={`tier-toggle-${t.id}`}>
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {plans.filter(p => {
                  const isTurboSelected = selectedPlan.startsWith('turbo');
                  return isTurboSelected ? (p.id === 'turbo_monthly' || p.id === 'turbo_annual') : (p.id === 'monthly' || p.id === 'annual');
                }).map(plan => {
                  const isSelected = selectedPlan === plan.id;
                  const isAnnual = plan.id === 'annual' || plan.id === 'turbo_annual';
                  const isTurbo = plan.id.startsWith('turbo');
                  const originalPrice = isTurbo
                    ? (isAnnual ? '19,999' : '1,999')
                    : null;
                  const effectiveMonthly = isAnnual
                    ? (isTurbo ? 'Rs. 833/month effective' : 'Rs. 249/month effective')
                    : null;
                  const borderClass = isSelected
                    ? (isTurbo ? 'border-fuchsia-500 bg-fuchsia-50/50' : 'border-sky-500 bg-sky-50/50')
                    : 'border-slate-200 hover:border-slate-300';
                  const checkColor = isTurbo ? 'text-fuchsia-600' : 'text-sky-600';
                  return (
                    <button key={plan.id} onClick={() => setSelectedPlan(plan.id)}
                      className={`relative rounded-xl border-2 p-4 text-left transition-all ${borderClass}`}
                      data-testid={`plan-${plan.id}`}>
                      {isAnnual && <span className="absolute -top-2.5 right-3 px-2.5 py-0.5 rounded-full bg-emerald-500 text-white text-[9px] font-bold">SAVE 17%</span>}
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-900">{plan.name}</span>
                        {isSelected && <Check size={16} className={checkColor} />}
                      </div>
                      <div className="flex items-baseline gap-2 mt-1 flex-wrap">
                        <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Rs. {plan.display_price}</span>
                        <span className="text-xs text-slate-500">/{isAnnual ? 'year' : 'month'}</span>
                        {originalPrice && <span className="text-xs text-slate-400 line-through">Rs. {originalPrice}</span>}
                      </div>
                      {effectiveMonthly && <p className="text-[10px] text-emerald-600 font-medium mt-0.5">{effectiveMonthly}</p>}
                      {isTurbo && !isAnnual && <p className="text-[10px] text-fuchsia-600 font-medium mt-0.5">Early-adopter price — first 1,000 doctors</p>}
                    </button>
                  );
                })}
              </div>
              <button onClick={() => setStep('pay')} className={`w-full mt-5 py-3.5 rounded-full text-white font-semibold text-sm flex items-center justify-center gap-2 ${selectedPlan.startsWith('turbo') ? 'bg-gradient-to-r from-fuchsia-600 to-rose-500 hover:from-fuchsia-700 hover:to-rose-600' : 'bg-sky-600 hover:bg-sky-700'}`} data-testid="proceed-to-pay-btn">
                <IndianRupee size={16} /> Pay Rs. {selectedPlanData?.display_price} via UPI
              </button>
              <p className="text-center text-[10px] text-slate-400 mt-2">
                {selectedPlan.startsWith('turbo')
                  ? 'TurboCharge pricing: Rs. 999/month or Rs. 9,999/year. Early-adopter price locked for first 1,000 doctors. Rs. 1,999/month after.'
                  : 'Coffee Premium pricing: Rs. 299/month or Rs. 2,999/year. See full Terms & Conditions for plan continuity rights.'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
